<template>
  <AppDrawer
    v-model="open"
    :title="t('login.forgotTitle')"
    title-align="left"
    padded
    body-padding="18px 18px 8px"
    :z-index="56"
    @back="closeForgotPassword"
  >
    <div class="step-dots">
      <i :class="{ on: step === 1 }"></i>
      <i :class="{ on: step === 2 }"></i>
    </div>
    <div class="logo-section">
      <span class="brand-mark">🔑</span>
      <h2>{{ t('login.forgotTitle') }}</h2>
      <p class="subtitle">{{ t('login.forgotSubtitle') }}</p>
    </div>

    <!-- 第一步：校验账号 + 已绑定手机/邮箱 + 验证码 -->
    <form
      v-if="step === 1"
      key="step1"
      class="forgot-form"
      @submit.prevent="onNextStep"
      @focusin="onFormFocusIn"
    >
      <div class="form-group">
        <label class="field-label">{{ t('login.accountLabel') }}</label>
        <div class="input-wrapper">
          <input
            v-model="form.account"
            type="text"
            autocomplete="username"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            enterkeyhint="next"
            :placeholder="t('login.accountPlaceholder')"
            class="form-input"
            @input="onAccountInput"
          />
        </div>
      </div>

      <div class="form-group">
        <label class="field-label">{{ t('login.boundContactLabel') }}</label>
        <div class="input-wrapper">
          <input
            v-model="form.contact"
            type="text"
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            enterkeyhint="next"
            :placeholder="t('login.boundContactPlaceholder')"
            class="form-input"
            @input="onContactInput"
          />
        </div>
      </div>

      <div class="form-group">
        <label class="field-label">{{ t('login.verifyCode') }}</label>
        <div class="input-wrapper">
          <input
            v-model="form.code"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            enterkeyhint="go"
            maxlength="6"
            :placeholder="t('login.sixDigitCodePlaceholder')"
            class="form-input"
            @input="onCodeInput"
          />
          <button type="button" class="code-btn" :disabled="codeCounting" @click="onSendCode">
            {{ codeCounting ? `${codeRemaining}s` : t('login.getCode') }}
          </button>
        </div>
      </div>

      <!-- 统一错误提示：仅显示在提交按钮上一行 -->
      <Transition name="err">
        <span v-if="formError" class="field-error" role="alert">{{ formError }}</span>
      </Transition>

      <button type="submit" class="submit-btn" :disabled="loading">
        <span v-if="loading" class="btn-spinner" aria-hidden="true" />
        {{ loading ? t('login.loading') : t('login.nextStep') }}
      </button>
    </form>

    <!-- 第二步：设置新登录密码 -->
    <form
      v-else
      key="step2"
      class="forgot-form"
      @submit.prevent="onConfirmReset"
      @focusin="onFormFocusIn"
    >
      <div class="form-group">
        <label class="field-label">{{ t('login.newPasswordLabel') }}</label>
        <div class="input-wrapper">
          <input
            v-model="form.newPassword"
            :type="showNewPassword ? 'text' : 'password'"
            maxlength="64"
            autocomplete="new-password"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            enterkeyhint="next"
            :placeholder="t('login.newPasswordPlaceholder')"
            class="form-input"
            @input="onAsciiInput"
          />
          <button
            type="button"
            class="toggle-password"
            :aria-label="showNewPassword ? 'Hide password' : 'Show password'"
            @click="showNewPassword = !showNewPassword"
          >
            <AppIcon :name="!showNewPassword ? 'eye-off' : 'eye'" class="ico" />
          </button>
        </div>
      </div>

      <div class="form-group">
        <label class="field-label">{{ t('login.confirmPasswordLabel') }}</label>
        <div class="input-wrapper">
          <input
            v-model="form.confirmPassword"
            :type="showConfirmPassword ? 'text' : 'password'"
            maxlength="64"
            autocomplete="new-password"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            enterkeyhint="go"
            :placeholder="t('login.confirmPasswordPlaceholder')"
            class="form-input"
            @input="onAsciiInput"
          />
          <button
            type="button"
            class="toggle-password"
            :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
            @click="showConfirmPassword = !showConfirmPassword"
          >
            <AppIcon :name="!showConfirmPassword ? 'eye-off' : 'eye'" class="ico" />
          </button>
        </div>
      </div>

      <Transition name="err">
        <span v-if="formError" class="field-error" role="alert">{{ formError }}</span>
      </Transition>

      <button type="submit" class="submit-btn" :disabled="loading">
        <span v-if="loading" class="btn-spinner" aria-hidden="true" />
        {{ loading ? t('login.loading') : t('login.confirmReset') }}
      </button>
    </form>

    <p class="remembered">
      {{ t('login.rememberedText') }}
      <a href="#" class="link" @click.prevent="onBackToLogin">{{ t('login.backToLogin') }}</a>
    </p>
  </AppDrawer>
</template>

<script setup lang="ts">
import AppIcon from '~/components/AppIcon.vue'

const { t } = useI18n()
const { open, prefillAccount, closeForgotPassword } = useForgotPasswordNav()
const { openLogin } = useAuthNav()
const { sendResetCode, verifyResetCode, confirmResetPassword } = useAuth()
const { success: notifySuccess } = useNotify()

const step = ref<1 | 2>(1)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

const form = reactive({
  account: '',
  contact: '',
  code: '',
  newPassword: '',
  confirmPassword: '',
})
const loading = ref(false)
const formError = ref('')

/** 输入事件即时清 DOM：IME 组合中的汉字等非法字符落框即抹掉（组合即中断）；watch(form) 兜底 */
const cleanInputEl = (event: Event, re: RegExp) => {
  const el = event.target as HTMLInputElement
  const cleaned = el.value.replace(re, '')
  if (cleaned !== el.value) el.value = cleaned
}
const onAsciiInput = (e: Event) => cleanInputEl(e, /[^\x21-\x7E]/g)
const onCodeInput = (e: Event) => cleanInputEl(e, /\D/g)
const onAccountInput = (e: Event) => cleanInputEl(e, /[^A-Za-z0-9_]/g)
/** 绑定手机/邮箱混合框：含字母或 @ 按邮箱结构矫正，纯数字按手机号规则 */
const onContactInput = (e: Event) => {
  const el = e.target as HTMLInputElement
  const cleaned = sanitizeContact(el.value)
  if (cleaned !== el.value) el.value = cleaned
}

/** 数据层兜底：粘贴/自动填充/程序化赋值同样被剥（用户名、联系方式、验证码、密码全字段） */
watch(form, () => {
  const account = form.account.replace(/[^A-Za-z0-9_]/g, '')
  if (account !== form.account) form.account = account

  const contact = sanitizeContact(form.contact)
  if (contact !== form.contact) form.contact = contact

  const code = form.code.replace(/\D/g, '')
  if (code !== form.code) form.code = code

  const newPassword = form.newPassword.replace(/[^\x21-\x7E]/g, '')
  if (newPassword !== form.newPassword) form.newPassword = newPassword

  const confirmPassword = form.confirmPassword.replace(/[^\x21-\x7E]/g, '')
  if (confirmPassword !== form.confirmPassword) form.confirmPassword = confirmPassword
})

const {
  isCounting: codeCounting,
  remaining: codeRemaining,
  send: sendCode,
} = useSmsCountdown(() => `forgot-password-code:${form.contact}`)

/** 绑定联系方式格式：邮箱 / E.164 手机号（后端 target 要求带区号，纯数字会发码失败） */
const isValidContact = (v: string) => isEmail(v) || isE164(v)

const onSendCode = () => {
  if (!isValidContact(form.contact)) {
    formError.value = t('login.errorContactRequired')
    return
  }
  formError.value = ''
  sendCode(() => sendResetCode(form.contact))
}

watch(step, () => {
  formError.value = ''
})

watch(open, val => {
  if (val && prefillAccount.value) form.account = prefillAccount.value
  else if (!val) resetForm()
})

const resetForm = () => {
  step.value = 1
  form.account = ''
  form.contact = ''
  form.code = ''
  form.newPassword = ''
  form.confirmPassword = ''
  formError.value = ''
  showNewPassword.value = false
  showConfirmPassword.value = false
}

const validateStep1 = (): boolean => {
  formError.value = ''

  if (!form.account) {
    formError.value = t('login.errorUsernameRequired')
  } else if (!isValidUsername(form.account)) {
    formError.value = t('login.errorUsernameInvalid')
  } else if (!form.contact || !isValidContact(form.contact)) {
    formError.value = t('login.errorContactRequired')
  } else if (!/^\d{6}$/.test(form.code)) {
    formError.value = t('login.errorEmailCodeRequired')
  }

  return !formError.value
}

const validateStep2 = (): boolean => {
  formError.value = ''

  if (!form.newPassword) {
    formError.value = t('login.errorNewPasswordRequired')
  } else if (!isValidPassword(form.newPassword)) {
    formError.value = t('login.errorNewPasswordInvalid')
  } else if (!form.confirmPassword) {
    formError.value = t('login.errorConfirmPasswordMismatch')
  } else if (form.confirmPassword !== form.newPassword) {
    formError.value = t('login.errorConfirmPasswordMismatch')
  }

  return !formError.value
}

/** 优先展示后端给的错误文案（验证码错误等），没有才用兜底文案；不再把「验证码校验失败」误报成「登录失败」 */
const toErrorText = (error: unknown, fallbackKey: string) => {
  if (import.meta.client && !navigator.onLine) return t('login.errorNetwork')
  return error instanceof Error && error.message ? error.message : t(fallbackKey)
}

/** 软键盘弹出后把当前输入框滚到可视区中部，避免被键盘遮住 */
const onFormFocusIn = (event: FocusEvent) => {
  const el = event.target as HTMLInputElement | null
  if (!el || el.tagName !== 'INPUT') return
  setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)
}

const onNextStep = async () => {
  if (!validateStep1()) return

  loading.value = true
  try {
    await verifyResetCode({
      account: form.account,
      contact: form.contact,
      code: form.code,
    })
    step.value = 2
  } catch (error) {
    formError.value = toErrorText(error, 'login.errorLoginFailed')
    console.error('Verify reset code failed:', error)
  } finally {
    loading.value = false
  }
}

const onConfirmReset = async () => {
  if (!validateStep2()) return

  loading.value = true
  try {
    await confirmResetPassword({
      account: form.account,
      contact: form.contact,
      code: form.code,
      newPassword: form.newPassword,
      confirmPassword: form.confirmPassword,
    })
    notifySuccess(t('login.resetSuccess'))
    await new Promise(resolve => setTimeout(resolve, 600))
    resetForm()
    onBackToLogin()
  } catch (error) {
    formError.value = toErrorText(error, 'login.errorResetFailed')
    console.error('Reset password failed:', error)
  } finally {
    loading.value = false
  }
}

const onBackToLogin = () => {
  closeForgotPassword()
  openLogin()
}
</script>

<style scoped lang="scss">
.logo-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;

  .handle-bar {
    width: 32px;
    height: 4px;
    border-radius: 999px;
    background: var(--primary);
    margin-bottom: 6px;
  }

  .brand-mark {
    width: 56px;
    height: 56px;
    border-radius: var(--r-lg);
    display: grid;
    place-items: center;
    color: #fff;
    font-weight: 900;
    font-size: 30px;
    background: var(--brand-mark);
    box-shadow: var(--brand-glow);
  }

  h2 {
    font-size: 20px;
    font-weight: 900;
  }

  .subtitle {
    font-size: 12px;
    color: var(--muted);
  }
}

.step-dots {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  margin-bottom: 8px;

  i.on {
    width: 32px;
    background: var(--primary);
  }

  i {
    width: 22px;
    height: 4px;
    border-radius: 3px;
    background: var(--hairline);
    transition:
      width 0.28s cubic-bezier(0.2, 0.8, 0.2, 1),
      background-color 0.2s ease-out;
  }
}

.forgot-form {
  animation: forgot-step-in 0.22s ease-out;
}

@keyframes forgot-step-in {
  from {
    opacity: 0;
    transform: translateX(14px);
  }
}

@keyframes forgot-spin {
  to {
    transform: rotate(360deg);
  }
}

.err-enter-active,
.err-leave-active {
  transition:
    opacity 0.2s ease-out,
    transform 0.2s ease-out;
}

.err-enter-from,
.err-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.form-group {
  margin-top: 14px;
}

.field-label {
  font-size: 12px;
  color: var(--muted);
  font-weight: 600;
  display: block;
  margin-bottom: 7px;
}

.field-error {
  display: block;
  margin-top: 6px;
  font-size: 11px;
  color: var(--danger, #e53935);
  font-weight: 600;
}

.input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--surface2);
  border: 1px solid var(--hairline);
  border-radius: var(--r-md);
  padding: 12px 14px;
  transition:
    border-color 0.18s ease-out,
    box-shadow 0.18s ease-out;

  &:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.18);
  }

  .form-input {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    color: var(--text);
    font-size: 15px;
    font-weight: 700;
  }

  .code-btn {
    padding: 8px 0 8px 8px;
    margin: -8px 0;
    color: var(--primary);
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;

    &:disabled {
      color: var(--muted);
      cursor: not-allowed;
    }

    &:active:not(:disabled) {
      opacity: 0.6;
    }
  }

  .toggle-password {
    display: grid;
    width: 36px;
    height: 36px;
    margin: -5px -7px -5px 0;
    place-items: center;
    padding: 0;
    color: var(--muted);

    &:active {
      opacity: 0.6;
    }

    .ico {
      font-size: 14px;
    }
  }
}

.btn-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 8px;
  vertical-align: -2px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: forgot-spin 0.7s linear infinite;
}

.submit-btn {
  margin-top: 16px;
  width: 100%;
  transition:
    transform 0.15s ease-out,
    opacity 0.2s ease-out;
  padding: 12px 16px;
  border-radius: 12px;
  background: var(--primary);
  box-shadow: var(--btn-glow);
  color: #fff;
  font-size: 14px;
  font-weight: 800;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
    transition-duration: 0.06s;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-dots i,
  .forgot-form,
  .input-wrapper,
  .submit-btn,
  .btn-spinner,
  .err-enter-active,
  .err-leave-active {
    animation: none !important;
    transition: none !important;
  }
}

.remembered {
  margin-top: 16px;
  text-align: center;
  font-size: 12px;
  color: var(--muted);

  .link {
    color: var(--primary);
    font-weight: 700;
  }
}
</style>
